import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import apiClient from '../services/apiClient';
import { roundToTwoDecimals, calculateSalesTaxByScenario } from '../utils/mathUtils';
import DynamicFieldToggle from '../components/DynamicFieldToggle';
import HSCodeSearchWithUoM from '../components/HSCodeSearchWithUoM';
import { FBRInvoiceHeader } from '../components/FBRLogo';
import { formatInvoiceDate, formatCurrency, formatSalesTax } from '../utils/invoiceUtils';

// Helper function to calculate furtherTax (4% for Unregistered buyers, except for SN008/SN027)
const calculateFurtherTax = (baseValue, scenario, buyerRegistrationType) => {
  // For 3rd Schedule scenarios (SN008, SN027): Further Tax NOT applicable (0%)
  if (["SN008", "SN027"].includes(scenario)) {
    return 0;
  }
  // For other scenarios: Only apply 4% if buyer is unregistered
  if (buyerRegistrationType === 'Unregistered') {
    return roundToTwoDecimals(baseValue * 0.04);
  }
  // For registered buyers: No further tax
  return 0;
};

// Helper function to calculate values based on single unit price and tax inclusion
const calculateItemValues = (singleUnitPrice, quantity, taxRate, priceIncludesTax, selectedScenario) => {
  const unitPrice = roundToTwoDecimals(singleUnitPrice);
  const qty = roundToTwoDecimals(quantity);
  const rate = roundToTwoDecimals(taxRate);

  // Check if this is a 3rd Schedule scenario
  const is3rdSchedule = ["SN008", "SN027"].includes(selectedScenario);

  let valueSalesExcludingST, salesTaxApplicable, totalValues, unitPriceExcludingTax, fixedNotifiedValueOrRetailPrice;

  if (is3rdSchedule) {
    // 3rd Schedule Goods: Tax calculated on Fixed Notified Value, not transaction value
    // For 3rd Schedule: singleUnitPrice is the MRP (Maximum Retail Price)

    // ✅ CORRECT FORMULA: fixedNotifiedValueOrRetailPrice = (quantity * singleUnitPrice) / (1 + taxRate / 100)
    fixedNotifiedValueOrRetailPrice = roundToTwoDecimals((qty * unitPrice) / (1 + rate / 100));

    // For 3rd Schedule (SN008, SN027): valueSalesExcludingST = fixedNotifiedValueOrRetailPrice
    valueSalesExcludingST = fixedNotifiedValueOrRetailPrice;

    // Calculate sales tax based on the fixed notified value
    salesTaxApplicable = roundToTwoDecimals(fixedNotifiedValueOrRetailPrice * (rate / 100));

    // Total Values = MRP × quantity (the original price including tax)
    totalValues = roundToTwoDecimals(unitPrice * qty);

    // Unit Price Excluding Tax = fixedNotifiedValueOrRetailPrice / quantity
    unitPriceExcludingTax = roundToTwoDecimals(fixedNotifiedValueOrRetailPrice / qty);

  } else {
    // Standard-Rated Goods: Normal calculation
    fixedNotifiedValueOrRetailPrice = 0; // Not applicable for standard goods

    if (priceIncludesTax) {
      // CORRECTED: For tax-inclusive pricing (based on suggest.txt analysis)
      // When priceIncludesTax=true, singleUnitPrice includes tax
      // Step 1: Calculate unitPriceExcludingTax = singleUnitPrice / (1 + rate)
      unitPriceExcludingTax = roundToTwoDecimals(unitPrice / (1 + rate / 100));

      // Step 2: Calculate valueSalesExcludingST = unitPriceExcludingTax × quantity
      valueSalesExcludingST = roundToTwoDecimals(unitPriceExcludingTax * qty);

      // Step 3: Calculate salesTaxApplicable using scenario-based method
      const itemForCalculation = {
        valueSalesExcludingST: valueSalesExcludingST,
        quantity: 1, // Already multiplied above
        saleType: 'Goods at standard rate (default)'
      };
      salesTaxApplicable = calculateSalesTaxByScenario(itemForCalculation, rate, selectedScenario);

      // Step 4: totalValues = singleUnitPrice × quantity (the tax-inclusive total)
      totalValues = roundToTwoDecimals(unitPrice * qty);
    } else {
      // If price excludes tax, standard calculation
      unitPriceExcludingTax = unitPrice; // Same as input when excluding tax
      valueSalesExcludingST = roundToTwoDecimals(unitPrice * qty);

      // Use scenario-based calculation
      const itemForCalculation = {
        valueSalesExcludingST: valueSalesExcludingST,
        quantity: 1, // Already multiplied above
        saleType: 'Goods at standard rate (default)'
      };
      salesTaxApplicable = calculateSalesTaxByScenario(itemForCalculation, rate, selectedScenario);
      totalValues = roundToTwoDecimals(valueSalesExcludingST + salesTaxApplicable);
    }
  }

  return {
    valueSalesExcludingST,
    salesTaxApplicable,
    totalValues,
    unitPriceExcludingTax,
    fixedNotifiedValueOrRetailPrice
  };
};

// Display-only mapping for SN rate text
const scenarioRateText = {
  SN001: '18%', SN002: '18%', SN003: '18%', SN004: '18%', SN005: '1%', SN006: '0%', SN007: '0%',
  SN008: '18%', SN009: '18%', SN010: '17%', SN011: '18%', SN012: '1.43%', SN013: '5%', SN014: '18%', SN015: '18%',
  SN016: '5%', SN017: '8%', SN018: '8%', SN019: '5%', SN020: '1%', SN021: 'Rs. 3', SN022: '18% + Rs 60 /KG', SN023: 'Rs. 200',
  SN024: '25%', SN025: '0%', SN026: '18%', SN027: '18%', SN028: '1%'
};
const getScenarioRateText = (sid) => scenarioRateText[sid] || '18%';

const parseRatePercent = (rate) => {
  const numericRate = parseFloat(String(rate || '').replace('%', '').trim());
  return Number.isFinite(numericRate) ? numericRate : null;
};

const normalizeHSCode = (hsCode) => {
  const rawHsCode = String(hsCode || '').trim();
  const compactHsCode = rawHsCode.replace(/[^0-9]/g, '');
  return compactHsCode.length === 8
    ? `${compactHsCode.slice(0, 4)}.${compactHsCode.slice(4)}`
    : rawHsCode;
};

const isPhotovoltaicHSCode = (hsCode) => {
  return ['8541.4200', '8541.4300'].includes(normalizeHSCode(hsCode));
};

const NOTE_FONT_SIZE_STORAGE_KEY = 'scenarioInvoiceNoteFontSize';
const DEFAULT_NOTE_FONT_SIZE = 12;
const MIN_NOTE_FONT_SIZE = 8;
const MAX_NOTE_FONT_SIZE = 24;

const applyKnownSROCorrection = (item, scenarioId) => {
  const ratePercent = parseRatePercent(item?.rate || item?.taxRateText || getScenarioRateText(scenarioId));

  if (ratePercent === 10 && isPhotovoltaicHSCode(item?.hsCode)) {
    return {
      ...item,
      sroScheduleNo: 'EIGHTH SCHEDULE Table 1',
      sroItemSerialNo: '90'
    };
  }

  return item;
};

// Helper: clean items for FBR payload (rate as text, remove extra fields)
const cleanItemsForFBR = (items, scenarioId) => (items || []).map((it) => {
  const { salesTaxRate, ratePercentage, unitPriceExcludingTax, ...rest } = it || {};
  const selectedRate = it?.rate || it?.taxRateText || getScenarioRateText(scenarioId);

  return applyKnownSROCorrection({
    ...rest,
    rate: typeof selectedRate === 'number' ? `${selectedRate}%` : selectedRate
  }, scenarioId);
});

const ScenarioInvoiceGenerator = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const invoiceRef = useRef();
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [scenarioDetails, setScenarioDetails] = useState(null);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('select');
  const [userProfile, setUserProfile] = useState(null);
  const [fieldConfig, setFieldConfig] = useState({});
  const [noteFontSize, setNoteFontSize] = useState(() => {
    const savedSize = Number(localStorage.getItem(NOTE_FONT_SIZE_STORAGE_KEY));
    return Number.isFinite(savedSize)
      ? Math.min(MAX_NOTE_FONT_SIZE, Math.max(MIN_NOTE_FONT_SIZE, savedSize))
      : DEFAULT_NOTE_FONT_SIZE;
  });
  // const [showJsonData, setShowJsonData] = useState(false); // REMOVED - JSON preview hidden

  // License Status
  const [licenseStatus, setLicenseStatus] = useState(null);

  // Scenario Access Summary
  const [accessSummary, setAccessSummary] = useState(null);
  const [visibleFields, setVisibleFields] = useState({
    rate: true,
    uoM: true,
    fixedNotifiedValueOrRetailPrice: false,
    salesTaxWithheldAtSource: false,
    extraTax: false,
    furtherTax: false,
    sroScheduleNo: false,
    fedPayable: false,
    discount: false,
    saleType: true,
    sroItemSerialNo: false
  });

  // Buyer Management Popup State
  const [showBuyerPopup, setShowBuyerPopup] = useState(false);
  const [buyerSearchTerm, setBuyerSearchTerm] = useState('');
  const [savedBuyers, setSavedBuyers] = useState([]);
  const [filteredBuyers, setFilteredBuyers] = useState([]);
  const [newBuyerForm, setNewBuyerForm] = useState({
    buyerNTNCNIC: '',
    buyerBusinessName: '',
    buyerProvince: 'Punjab',
    buyerAddress: '',
    buyerRegistrationType: 'Unregistered'
  });
  const [showAddBuyerForm, setShowAddBuyerForm] = useState(false);

  // Custom form data
  const [customData, setCustomData] = useState({
    invoiceDate: new Date().toISOString().split('T')[0],
    invoiceRefNo: '',
    reason: '',
    invoiceType: 'Sale Invoice',
    scenarioId: '',
    // Seller Information
    sellerNTNCNIC: '',
    sellerBusinessName: '',
    sellerProvince: 'Punjab',
    sellerAddress: '',
    // Buyer Information
    buyerNTNCNIC: '9999999',
    buyerBusinessName: 'NA',
    buyerProvince: 'Punjab',
    buyerAddress: 'NA',
    buyerRegistrationType: 'Unregistered',
    // Item Configuration
    saleType: 'Goods at standard rate (default)',
    // Note field (not part of JSON data, stored separately)
    note: '',
    // Legacy fields for backward compatibility
    customSeller: {},
    customBuyer: {},
    customItems: []
  });

  // Reference data from database
  const [referenceData, setReferenceData] = useState({
    invoice_type: [],
    province: [],
    registration_type: [],
    uom: [],
    sale_type: []
  });

  // Load license status
  const loadLicenseStatus = async () => {
    try {
      const response = await apiClient.get('/auth/license-status');
      setLicenseStatus(response.data);
    } catch (error) {
      console.error('Failed to load license status:', error);
      setLicenseStatus({
        hasLicense: false,
        hasFBRKeys: false,
        canSubmitInvoices: false,
        message: 'Unable to load license status'
      });
    }
  };

  useEffect(() => {
    localStorage.setItem(NOTE_FONT_SIZE_STORAGE_KEY, String(noteFontSize));
  }, [noteFontSize]);

  // ✅ Recalculate furtherTax for all items when buyerRegistrationType or selectedScenario changes
  useEffect(() => {
    setCustomData((previousData) => {
      const updatedItems = previousData.customItems.map((item) => {
        const baseValue = item.fixedNotifiedValueOrRetailPrice || item.valueSalesExcludingST || 0;
        const furtherTax = calculateFurtherTax(baseValue, selectedScenario, previousData.buyerRegistrationType);
        return item.furtherTax === furtherTax ? item : { ...item, furtherTax };
      });
      const changed = updatedItems.some((item, index) => item !== previousData.customItems[index]);
      return changed ? { ...previousData, customItems: updatedItems } : previousData;
    });
  }, [selectedScenario, customData.buyerRegistrationType]);

  useEffect(() => {
    if (!customData.customItems || customData.customItems.length === 0) {
      return;
    }

    const updatedItems = customData.customItems.map(item => applyKnownSROCorrection(item, selectedScenario));
    const hasSROChanges = updatedItems.some((item, index) =>
      item.sroScheduleNo !== customData.customItems[index].sroScheduleNo ||
      item.sroItemSerialNo !== customData.customItems[index].sroItemSerialNo
    );

    if (hasSROChanges) {
      setCustomData(prev => ({ ...prev, customItems: updatedItems }));
    }
  }, [selectedScenario, customData.customItems]);


  const handleInvoiceSearch = async () => {
    if (!customData.invoiceRefNo) {
      alert('Please enter an Invoice Reference Number');
      return;
    }

    try {
      setLoading(true);
      // Search by FBR Invoice Number
      const response = await api.get('/invoice', {
        params: {
          fbrInvoiceNumber: customData.invoiceRefNo,
          limit: 1
        }
      });

      if (response.data.invoices && response.data.invoices.length > 0) {
        const invoice = response.data.invoices[0];

        // Fetch full invoice details to get items
        const detailResponse = await api.get(`/invoice/${invoice.id}`);
        const fullInvoice = detailResponse.data.invoice;

        // Populate fields
        setCustomData(prev => ({
          ...prev,
          buyerNTNCNIC: fullInvoice.buyer_ntn_cnic || '',
          buyerBusinessName: fullInvoice.buyer_business_name || '',
          buyerProvince: fullInvoice.buyer_province || '',
          buyerAddress: fullInvoice.buyer_address || '',
          buyerRegistrationType: fullInvoice.buyer_registration_type || 'Unregistered',
          customItems: (fullInvoice.items || []).map(item => ({
            hsCode: item.hs_code || item.hsCode || '',
            productDescription: item.product_description || item.productDescription || '',
            rate: item.rate || '',
            taxRateText: item.rate || '',
            uoM: item.uom || item.uoM || 'NOS',
            quantity: item.quantity || '',
            totalValues: item.total_values || item.totalValues || '',
            valueSalesExcludingST: item.value_sales_excluding_st || item.valueSalesExcludingST || '',
            fixedNotifiedValueOrRetailPrice: item.fixed_notified_value_or_retail_price || item.fixedNotifiedValueOrRetailPrice || '',
            salesTaxApplicable: item.sales_tax_applicable || item.salesTaxApplicable || '',
            salesTaxWithheldAtSource: item.sales_tax_withheld_at_source || item.salesTaxWithheldAtSource || '0',
            extraTax: item.extra_tax || item.extraTax || '0',
            furtherTax: item.further_tax || item.furtherTax || '0',
            sroScheduleNo: item.sro_schedule_no || item.sroScheduleNo || '',
            fedPayable: item.fed_payable || item.fedPayable || '0',
            discount: item.discount || '0',
            saleType: item.sale_type || item.saleType || '',
            sroItemSerialNo: item.sro_item_serial_no || item.sroItemSerialNo || '',
            unitPriceExcludingTax: '',
            singleUnitPrice: '',
            taxCalculationType: 'exclusive'
          }))
        }));

        alert('Invoice found and details populated');
      } else {
        alert('Invoice not found');
      }
    } catch (error) {
      console.error('Error searching invoice:', error);
      alert('Error searching invoice');
    } finally {
      setLoading(false);
    }
  };

  const loadUserProfile = async () => {
    try {
      const response = await api.user.getProfile();
      setUserProfile(response.data.profile);

      // Auto-populate seller information from user profile
      const sellerInfo = response.data.sellerInfo;
      setCustomData(prev => ({
        ...prev,
        sellerNTNCNIC: sellerInfo.sellerNTNCNIC || '',
        sellerBusinessName: sellerInfo.sellerBusinessName || '',
        sellerProvince: sellerInfo.sellerProvince || 'Punjab',
        sellerAddress: sellerInfo.sellerAddress || ''
      }));
    } catch (err) {
      console.error('Failed to load user profile:', err);
      // Don't show error to user as this is not critical
    }
  };

  const loadReferenceData = async () => {
    const fallbackData = {
      invoice_type: [
        { code: 'SALE', description: 'Sale Invoice' },
        { code: 'CREDIT', description: 'Credit Note' },
        { code: 'DEBIT', description: 'Debit Note' },
        { code: 'RETURN', description: 'Return Invoice' }
      ],
      province: [
        { code: 'PB', description: 'Punjab' },
        { code: 'SD', description: 'Sindh' },
        { code: 'KPK', description: 'Khyber Pakhtunkhwa' },
        { code: 'BL', description: 'Balochistan' },
        { code: 'ICT', description: 'Islamabad Capital Territory' },
        { code: 'GB', description: 'Gilgit-Baltistan' },
        { code: 'AJK', description: 'Azad Jammu and Kashmir' }
      ],
      registration_type: [
        { code: 'REG', description: 'Registered' },
        { code: 'UNREG', description: 'Unregistered' },
        { code: 'EXPORT', description: 'Export' },
        { code: 'GOVT', description: 'Government' },
        { code: 'DIPLO', description: 'Diplomatic' }
      ],
      uom: [
        { code: 'NOS', description: 'Numbers, pieces, units' },
        { code: 'KG', description: 'KG' },
        { code: 'GM', description: 'Grams' },
        { code: 'LTR', description: 'Liter' },
        { code: 'MTR', description: 'Meter' }
      ],
      sale_type: [
        { code: 'STD', description: 'Goods at standard rate (default)' },
        { code: 'ZERO', description: 'Zero rated supplies' },
        { code: 'EXEMPT', description: 'Exempt supplies' },
        { code: 'NON_ADJ', description: 'Non-Adjustable Supplies' }
      ]
    };

    try {
      console.log('📋 Loading reference data from database...');
      const response = await api.referenceData.getAll();
      const data = response.data.data;

      // Check if data is empty or missing keys, and merge with fallback
      const mergedData = {
        invoice_type: (data.invoice_type && data.invoice_type.length > 0) ? data.invoice_type : fallbackData.invoice_type,
        province: (data.province && data.province.length > 0) ? data.province : fallbackData.province,
        registration_type: (data.registration_type && data.registration_type.length > 0) ? data.registration_type : fallbackData.registration_type,
        uom: (data.uom && data.uom.length > 0) ? data.uom : fallbackData.uom,
        sale_type: (data.sale_type && data.sale_type.length > 0) ? data.sale_type : fallbackData.sale_type
      };

      console.log('✅ Reference data loaded (merged):', mergedData);
      setReferenceData(mergedData);
    } catch (err) {
      console.error('Failed to load reference data:', err);
      // Fallback to hardcoded data if database fails
      setReferenceData(fallbackData);
    }
  };

  const loadScenarios = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.scenarios.getAll();
      setScenarios(response.data.scenarios || []);

      const accessResponse = await api.scenarios.getAccessSummary();
      setAccessSummary(accessResponse.data);
    } catch (err) {
      setError('Failed to load scenarios: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadScenarios();
    loadUserProfile();
    loadReferenceData();
    loadLicenseStatus();
  }, [loadScenarios]);

  const loadScenarioDetails = async (scenarioId) => {
    try {
      setLoading(true);
      const response = await api.scenarios.getById(scenarioId);
      const scenario = response.data.scenario;

      setScenarioDetails(scenario);
      setSelectedScenario(scenarioId);

      // Set field configuration from scenario
      if (scenario.fieldConfig) {
        setFieldConfig(scenario.fieldConfig);

        // Update visible fields based on scenario configuration
        const newVisibleFields = { ...visibleFields };
        Object.keys(scenario.fieldConfig).forEach(fieldName => {
          const config = scenario.fieldConfig[fieldName];
          newVisibleFields[fieldName] = config.visible || config.required || config.hasData;
        });

        // Automatically show fixedNotifiedValueOrRetailPrice for SN008 and SN027 (3rd Schedule Goods)
        if (scenarioId === 'SN008' || scenarioId === 'SN027') {
          newVisibleFields.fixedNotifiedValueOrRetailPrice = true;
          console.log(`📋  ${scenarioId}: Automatically showing fixedNotifiedValueOrRetailPrice field`);
        }

        setVisibleFields(newVisibleFields);
      }

      // Auto-populate fields based on scenario and user profile
      if (scenario.defaultData.items && scenario.defaultData.items[0]) {
        const defaultItem = scenario.defaultData.items[0];
        setCustomData(prev => ({
          ...prev,
          scenarioId: scenarioId,
          saleType: defaultItem.saleType || 'Goods at standard rate (default)',
          // Auto-populate buyer registration type from scenario
          buyerRegistrationType: scenario.defaultData.buyerRegistrationType || 'Unregistered',
          // Auto-populate seller information from user profile (will be handled by backend)
          sellerNTNCNIC: userProfile?.ntn_cnic || '',
          sellerBusinessName: userProfile?.business_name || '',
          sellerProvince: userProfile?.province || 'Punjab',
          sellerAddress: userProfile?.address || '',
          // Pre-populate item fields from scenario
          customItems: [{
            ...defaultItem,
            // Ensure all fields are present with default values
            rate: defaultItem.rate || '18%',
            uoM: defaultItem.uoM || 'Numbers, pieces, units',
            // Initialize new fields
            singleUnitPrice: defaultItem.valueSalesExcludingST || 0, // Initialize with existing unit price
            priceIncludesTax: ["SN008", "SN027"].includes(selectedScenario) ? true : false, // Force true for 3rd Schedule
            unitPriceExcludingTax: defaultItem.valueSalesExcludingST || 0, // For display
            // Calculate salesTaxApplicable as amount, not rate
            salesTaxApplicable: (() => {
              const singleUnitPrice = defaultItem.valueSalesExcludingST || 0;
              const quantity = defaultItem.quantity || 1;
              const taxRate = parseFloat((defaultItem.rate || '18%').replace('%', ''));
              // valueSalesExcludingST = singleUnitPrice * quantity
              const valueSalesExcludingST = singleUnitPrice * quantity;
              return roundToTwoDecimals((valueSalesExcludingST * taxRate) / 100);
            })(),
            salesTaxWithheldAtSource: defaultItem.salesTaxWithheldAtSource || 0,
            extraTax: defaultItem.extraTax || '',
            furtherTax: defaultItem.furtherTax || 0,
            sroScheduleNo: defaultItem.sroScheduleNo || '',
            fedPayable: defaultItem.fedPayable || 0,
            discount: defaultItem.discount || 0,
            saleType: defaultItem.saleType || 'Goods at standard rate (default)',
            sroItemSerialNo: defaultItem.sroItemSerialNo || '',
            // Set fixedNotifiedValueOrRetailPrice based on scenario requirements
            fixedNotifiedValueOrRetailPrice: ["SN008", "SN027"].includes(selectedScenario)
              ? ((defaultItem.valueSalesExcludingST || 0) * (defaultItem.quantity || 1)) // Unit price × quantity for SN008 and SN027
              : 0 // Zero for all other scenarios
          }]
        }));
      }

      setActiveTab('generate');
    } catch (err) {
      setError('Failed to load scenario details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateInvoice = async () => {
    if (!selectedScenario) return;

    // Validation for Debit Note
    if (customData.invoiceType === 'Debit Note') {
      if (!customData.invoiceRefNo) {
        setError('Invoice Reference Number is required for Debit Note');
        return;
      }
      if (!customData.reason) {
        setError('Reason is required for Debit Note');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.scenarios.generate(selectedScenario, customData);
      setGeneratedInvoice(response.data);
      setActiveTab('preview');
    } catch (err) {
      setError('Failed to generate invoice: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Client-side UoM validation (fallback)
  const getClientSideUoMValidation = (hsCode, uom) => {
    const isTextile = hsCode && (
      hsCode.startsWith('50') || hsCode.startsWith('51') || hsCode.startsWith('52') ||
      hsCode.startsWith('53') || hsCode.startsWith('54') || hsCode.startsWith('55') ||
      hsCode.startsWith('56') || hsCode.startsWith('57') || hsCode.startsWith('58') ||
      hsCode.startsWith('59') || hsCode.startsWith('60') || hsCode.startsWith('61') ||
      hsCode.startsWith('62') || hsCode.startsWith('63')
    );

    const isNumbersUoM = uom === 'Numbers, pieces, units';

    if (isTextile && isNumbersUoM) {
      return {
        isValid: false,
        error: `UoM "${uom}" is not allowed for textile HS Code ${hsCode}`,
        category: 'Textiles and Fabrics',
        suggestedUoM: 'KG',
        validUoMs: ['KG', 'Meter', 'Square meters']
      };
    }

    return {
      isValid: true,
      error: null,
      category: isTextile ? 'Textiles and Fabrics' : 'Other Products',
      suggestedUoM: null,
      validUoMs: isTextile ? ['KG', 'Meter', 'Square meters'] : ['Numbers, pieces, units', 'Pieces', 'Sets', 'KG']
    };
  };

  const validateInvoice = async () => {
    if (!generatedInvoice) return;

    // Check license status before validation
    if (!licenseStatus?.canSubmitInvoices) {
      setError('License required: You need an active API Key License and FBR API keys to validate invoices. Please contact administrator or configure your FBR settings.');
      return;
    }

    // Validation for Debit Note
    if (customData.invoiceType === 'Debit Note') {
      if (!customData.invoiceRefNo) {
        setError('Invoice Reference Number is required for Debit Note');
        return;
      }
      if (!customData.reason) {
        setError('Reason is required for Debit Note');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      // First, validate UoM for all items
      console.log('📋  Validating UoM for all items before FBR submission...');
      const uomValidationErrors = [];

      for (let i = 0; i < generatedInvoice.invoiceData.items.length; i++) {
        const item = generatedInvoice.invoiceData.items[i];
        try {
          const uomResponse = await api.reference.validateUoM({
            hsCode: item.hsCode,
            uom: item.uoM
          });

          if (!uomResponse.data.validation.isValid) {
            uomValidationErrors.push({
              itemIndex: i + 1,
              hsCode: item.hsCode,
              currentUoM: item.uoM,
              error: uomResponse.data.validation.error,
              suggestedUoM: uomResponse.data.validation.suggestedUoM,
              validUoMs: uomResponse.data.validation.validUoMs
            });
          }
        } catch (uomError) {
          console.warn(`UoM validation failed for item ${i + 1}:`, uomError);
          // Fallback to client-side validation
          const clientSideValidation = getClientSideUoMValidation(item.hsCode, item.uoM);
          if (!clientSideValidation.isValid) {
            uomValidationErrors.push({
              itemIndex: i + 1,
              hsCode: item.hsCode,
              currentUoM: item.uoM,
              error: clientSideValidation.error,
              suggestedUoM: clientSideValidation.suggestedUoM,
              validUoMs: clientSideValidation.validUoMs
            });
          }
        }
      }

      // Show warnings instead of auto-fixing UoM
      if (uomValidationErrors.length > 0) {
        const warningMessage = `⚠️  UoM Optimization Warning:\n\n` +
          uomValidationErrors.map(error =>
            `Item ${error.itemIndex} (${error.hsCode}): "${error.currentUoM}" may not be optimal. Suggested: "${error.suggestedUoM}"`
          ).join('\n') +
          '\n\nProceeding with current values as per your selection...';

        console.log('⚠️ UoM Warning:', warningMessage);
        // We no longer auto-fix here to respect the user's/FBR's selection
      }

      console.log('✅ All UoM validations passed, proceeding with FBR validation...');

      // Merge the current custom data (buyer information) with the generated invoice data
      const updatedInvoiceData = {
        ...generatedInvoice.invoiceData,
        // Override with current invoice header information
        invoiceType: customData.invoiceType,
        invoiceRefNo: customData.invoiceRefNo,
        reason: customData.reason,
        invoiceDate: customData.invoiceDate,
        // Override with current buyer information from the UI
        buyerBusinessName: customData.buyerBusinessName || 'Walk In Customer',
        buyerAddress: customData.buyerAddress || 'Walk In Customer Address',
        buyerNTNCNIC: customData.buyerNTNCNIC || '9999999',
        buyerProvince: customData.buyerProvince || 'Punjab',
        buyerRegistrationType: customData.buyerRegistrationType || 'Unregistered',
        items: cleanItemsForFBR(generatedInvoice.invoiceData.items, selectedScenario)
      };

      console.log('📋  Validating invoice with updated buyer data:', {
        buyerBusinessName: updatedInvoiceData.buyerBusinessName,
        buyerAddress: updatedInvoiceData.buyerAddress,
        buyerNTNCNIC: updatedInvoiceData.buyerNTNCNIC,
        buyerProvince: updatedInvoiceData.buyerProvince
      });

      const response = await api.scenarios.validate(selectedScenario, {
        invoiceData: updatedInvoiceData,
        note: customData.note // Include the note field for consistency
      });

      setValidationResult(response.data);
      setActiveTab('validate');
    } catch (err) {
      setError('Failed to validate invoice: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitInvoice = async () => {
    if (!generatedInvoice) return;

    // Check license status before submission
    if (!licenseStatus?.canSubmitInvoices) {
      setError('License required: You need an active API Key License and FBR API keys to submit invoices. Please contact administrator or configure your FBR settings.');
      return;
    }

    // Validation for Debit Note
    if (customData.invoiceType === 'Debit Note') {
      if (!customData.invoiceRefNo) {
        setError('Invoice Reference Number is required for Debit Note');
        return;
      }
      if (!customData.reason) {
        setError('Reason is required for Debit Note');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      // Merge the current custom data (buyer information) with the generated invoice data
      const updatedInvoiceData = {
        ...generatedInvoice.invoiceData,
        // Override with current invoice header information
        invoiceType: customData.invoiceType,
        invoiceRefNo: customData.invoiceRefNo,
        reason: customData.reason,
        invoiceDate: customData.invoiceDate,
        // Override with current buyer information from the UI
        buyerBusinessName: customData.buyerBusinessName || 'Walk In Customer',
        buyerAddress: customData.buyerAddress || 'Walk In Customer Address',
        buyerNTNCNIC: customData.buyerNTNCNIC || '9999999',
        buyerProvince: customData.buyerProvince || 'Punjab',
        buyerRegistrationType: customData.buyerRegistrationType || 'Unregistered',
        items: cleanItemsForFBR(generatedInvoice.invoiceData.items, selectedScenario)
      };

      console.log('📋  Submitting invoice with updated buyer data:', {
        buyerBusinessName: updatedInvoiceData.buyerBusinessName,
        buyerAddress: updatedInvoiceData.buyerAddress,
        buyerNTNCNIC: updatedInvoiceData.buyerNTNCNIC,
        buyerProvince: updatedInvoiceData.buyerProvince
      });

      const response = await api.scenarios.submit(selectedScenario, {
        invoiceData: updatedInvoiceData,
        note: customData.note // Include the note field
      });

      // Update generated invoice with FBR-assigned invoice number if available
      if (response.data.fbrInvoiceNumber && response.data.updatedInvoiceData) {
        setGeneratedInvoice(prev => ({
          ...prev,
          invoiceData: response.data.updatedInvoiceData,
          fbrInvoiceNumber: response.data.fbrInvoiceNumber
        }));
      }

      setSubmissionResult(response.data);
      setActiveTab('submit');
    } catch (err) {
      setError('Failed to submit invoice: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedScenario(null);
    setScenarioDetails(null);
    setGeneratedInvoice(null);
    setValidationResult(null);
    setSubmissionResult(null);
    setActiveTab('select');
    setCustomData({
      invoiceDate: new Date().toISOString().split('T')[0],
      invoiceRefNo: '',
      reason: '',
      invoiceType: 'Sale Invoice',
      scenarioId: '',
      // Seller Information
      sellerNTNCNIC: '',
      sellerBusinessName: '',
      sellerProvince: 'Punjab',
      sellerAddress: 'Gujranwala',
      // Buyer Information
      buyerNTNCNIC: '9999999',
      buyerBusinessName: 'NA',
      buyerProvince: 'Punjab',
      buyerAddress: 'NA',
      buyerRegistrationType: 'Unregistered',
      // Item Configuration
      saleType: 'Goods at standard rate (default)',
      // Legacy fields for backward compatibility
      customSeller: {},
      customBuyer: {},
      customItems: []
    });
  };

  // Buyer Management Functions
  const loadSavedBuyers = async () => {
    try {
      const response = await apiClient.get('/customers');
      if (response.data && response.data.data) {
        setSavedBuyers(response.data.data);
        setFilteredBuyers(response.data.data);
      }
    } catch (error) {
      console.error('Error loading saved buyers:', error);
      setError('Failed to load buyers: ' + error.message);
    }
  };

  const saveBuyerToDatabase = async (buyerData) => {
    try {
      const customerData = {
        customer_type: 'regular',
        ntn_cnic: buyerData.buyerNTNCNIC,
        business_name: buyerData.buyerBusinessName,
        province: buyerData.buyerProvince,
        address: buyerData.buyerAddress,
        registration_type: buyerData.buyerRegistrationType === 'Unregistered' ? 'CNIC' : 'NTN'
      };

      // Validate required fields
      if (!customerData.business_name || customerData.business_name.trim() === '') {
        setError('Business name is required');
        return null;
      }

      const response = await apiClient.post('/customers', customerData);

      if (response.data && response.data.data) {
        // Reload buyers list
        await loadSavedBuyers();
        return response.data.data;
      }
    } catch (error) {
      console.error('Error saving buyer:', error);
      setError('Failed to save buyer: ' + (error.response?.data?.message || error.message));
      return null;
    }
  };

  const deleteBuyerFromDatabase = async (buyerId) => {
    try {
      const response = await apiClient.delete(`/customers/${buyerId}`);
      if (response.data && response.data.message) {
        // Reload buyers list
        await loadSavedBuyers();
      }
    } catch (error) {
      console.error('Error deleting buyer:', error);
      setError('Failed to delete buyer: ' + error.message);
    }
  };

  const handleBuyerSearch = async (searchTerm) => {
    setBuyerSearchTerm(searchTerm);
    try {
      const response = await apiClient.get(`/customers?search=${encodeURIComponent(searchTerm)}`);
      if (response.data && response.data.data) {
        setFilteredBuyers(response.data.data);
      }
    } catch (error) {
      console.error('Error searching buyers:', error);
      // Fallback to local filtering if API fails
      if (!searchTerm.trim()) {
        setFilteredBuyers(savedBuyers);
      } else {
        const filtered = savedBuyers.filter(buyer =>
          buyer.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          buyer.ntn_cnic.toLowerCase().includes(searchTerm.toLowerCase()) ||
          buyer.address.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredBuyers(filtered);
      }
    }
  };

  const selectBuyer = (buyer) => {
    setCustomData(prev => ({
      ...prev,
      buyerNTNCNIC: buyer.ntn_cnic,
      buyerBusinessName: buyer.business_name,
      buyerProvince: buyer.province,
      buyerAddress: buyer.address,
      buyerRegistrationType: buyer.registration_type === 'NTN' ? 'Registered' : 'Unregistered'
    }));
    setShowBuyerPopup(false);
    setBuyerSearchTerm('');
  };

  const selectWalkInCustomer = () => {
    setCustomData(prev => ({
      ...prev,
      buyerNTNCNIC: '9999999',
      buyerBusinessName: 'Walk In Customer',
      buyerProvince: 'Punjab',
      buyerAddress: 'N/A',
      buyerRegistrationType: 'Unregistered'
    }));
    setShowBuyerPopup(false);
    setBuyerSearchTerm('');
  };

  const handleAddNewBuyer = async () => {
    if (!newBuyerForm.buyerBusinessName.trim() || !newBuyerForm.buyerNTNCNIC.trim()) {
      alert('Please fill in Business Name and NTN/CNIC fields');
      return;
    }

    const savedBuyer = await saveBuyerToDatabase(newBuyerForm);
    if (savedBuyer) {
      selectBuyer(savedBuyer);
      setNewBuyerForm({
        buyerNTNCNIC: '',
        buyerBusinessName: '',
        buyerProvince: 'Punjab',
        buyerAddress: '',
        buyerRegistrationType: 'Unregistered'
      });
      setShowAddBuyerForm(false);
    }
  };

  const clearBuyerFields = () => {
    setCustomData(prev => ({
      ...prev,
      buyerNTNCNIC: '',
      buyerBusinessName: '',
      buyerProvince: 'Punjab',
      buyerAddress: '',
      buyerRegistrationType: 'Unregistered'
    }));
  };

  // Load saved buyers on component mount
  useEffect(() => {
    if (user) {
      loadSavedBuyers();
    }
  }, [user]);

  const downloadJSON = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">FBR Scenario Invoice Generator</h1>
              <p className="text-gray-600 mt-1">Generate FBR-compliant invoices based on predefined scenarios</p>
            </div>
            <button
              onClick={resetForm}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* License Status */}
        {licenseStatus && (
          <div className={`mx-6 mt-4 rounded-lg shadow-lg ${licenseStatus.canSubmitInvoices
            ? 'bg-green-50 border border-green-200'
            : 'bg-yellow-50 border border-yellow-200'
            }`}>
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`h-3 w-3 rounded-full mr-3 ${licenseStatus.canSubmitInvoices ? 'bg-green-400' : 'bg-yellow-400'
                    }`}></div>
                  <div>
                    <h3 className={`text-sm font-medium ${licenseStatus.canSubmitInvoices ? 'text-green-800' : 'text-yellow-800'
                      }`}>
                      {licenseStatus.canSubmitInvoices ? 'Ready for FBR Integration' : 'Setup Required'}
                    </h3>
                    <p className={`text-xs mt-1 ${licenseStatus.canSubmitInvoices ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                      {licenseStatus.message}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 text-xs">
                  <div className={`px-2 py-1 rounded ${licenseStatus.hasLicense && !licenseStatus.isLicenseExpired
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                    }`}>
                    License: {licenseStatus.hasLicense && !licenseStatus.isLicenseExpired ? 'Active' : 'Required'}
                  </div>
                  <div className={`px-2 py-1 rounded ${licenseStatus.hasFBRKeys ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                    FBR Keys: {licenseStatus.hasFBRKeys ? 'Configured' : 'Missing'}
                  </div>
                  <div className={`px-2 py-1 rounded ${(user?.api_environment || 'sandbox') === 'sandbox'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                    }`}>
                    Environment: {(user?.api_environment || 'sandbox') === 'sandbox' ? 'Sandbox' : 'Production'}
                  </div>
                  {licenseStatus.license && (
                    <div className="text-gray-600">
                      Usage: {licenseStatus.license.current_usage}/{licenseStatus.license.monthly_limit}
                    </div>
                  )}
                </div>
              </div>
              {!licenseStatus.canSubmitInvoices && (
                <div className="mt-3 text-xs text-yellow-700">
                  <p>To validate and submit invoices to FBR, you need:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    {!licenseStatus.hasLicense && <li>Active API Key License (contact administrator)</li>}
                    {licenseStatus.isLicenseExpired && <li>License renewal (contact administrator)</li>}
                    {!licenseStatus.hasFBRKeys && (
                      <li>
                        FBR API keys configured in{' '}
                        <button
                          onClick={() => navigate('/app/profile?tab=fbr-api')}
                          className="underline hover:text-yellow-800"
                        >
                          Profile Settings
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'select', name: 'Select Scenario', icon: '📋' },
              { id: 'generate', name: 'Generate Invoice', icon: '📋', disabled: !selectedScenario },
              { id: 'preview', name: 'Preview Invoice', icon: '📋', disabled: !generatedInvoice },
              { id: 'validate', name: 'Validate', icon: '✅', disabled: !generatedInvoice || !licenseStatus?.canSubmitInvoices },
              { id: 'submit', name: 'Submit', icon: '📋', disabled: !validationResult?.success || !licenseStatus?.canSubmitInvoices }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                className={`${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : tab.disabled
                    ? 'border-transparent text-gray-400 cursor-not-allowed'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                disabled={tab.disabled}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Select Scenario Tab */}
          {activeTab === 'select' && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Select FBR Scenario</h2>

              {/* Access Summary */}
              {accessSummary && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-blue-900">Your Scenario Access</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${accessSummary.hasBusinessProfile
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                      }`}>
                      {accessSummary.accessLevel}
                    </span>
                  </div>

                  {accessSummary.hasBusinessProfile ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-blue-700">Business Activity:</span>
                        <span className="font-medium text-blue-900">{accessSummary.businessActivity.name}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-blue-700">Business Sector:</span>
                        <span className="font-medium text-blue-900">{accessSummary.sector.name}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-blue-700">Applicable Scenarios:</span>
                        <span className="font-medium text-blue-900">
                          {accessSummary.totalApplicableScenarios} of {accessSummary.totalSystemScenarios}
                        </span>
                      </div>
                      {accessSummary.restrictedScenarios > 0 && (
                        <div className="text-xs text-blue-600 mt-2">
                          {accessSummary.restrictedScenarios} scenarios are restricted based on your business profile.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-yellow-700">
                      <p className="mb-2">{accessSummary.message}</p>
                      <p className="text-xs">
                        Update your profile to see only scenarios applicable to your business.
                      </p>
                    </div>
                  )}
                </div>
              )}
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {scenarios.map((scenario) => (
                    <div
                      key={scenario.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                      onClick={() => loadScenarioDetails(scenario.id)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-900">{scenario.id}</h3>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          FBR Scenario
                        </span>
                      </div>
                      <h4 className="font-medium text-gray-800 mb-2">{scenario.name}</h4>
                      <p className="text-sm text-gray-600">{scenario.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Generate Invoice Tab */}
          {activeTab === 'generate' && scenarioDetails && (
            <div>
              <h2 className="text-lg font-semibold mb-4">
                Generate Invoice - {scenarioDetails.name} ({scenarioDetails.id})
              </h2>

              <div className="space-y-6">
                {/* Basic Invoice Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">Invoice Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Invoice Date
                      </label>
                      <input
                        type="date"
                        value={customData.invoiceDate}
                        onChange={(e) => setCustomData({ ...customData, invoiceDate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Invoice Reference No.
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={customData.invoiceRefNo || ''}
                          onChange={(e) => setCustomData({ ...customData, invoiceRefNo: e.target.value })}
                          placeholder={`INV-${scenarioDetails.id}-${Date.now()}`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {customData.invoiceType === 'Debit Note' && (
                          <button
                            type="button"
                            onClick={handleInvoiceSearch}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            title="Search Invoice"
                          >
                            🔍
                          </button>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Invoice Type
                      </label>
                      <select
                        value={customData.invoiceType}
                        onChange={(e) => setCustomData({ ...customData, invoiceType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Sale Invoice">Sale Invoice</option>
                        <option value="Purchase Invoice">Purchase Invoice</option>
                        <option value="Debit Note">Debit Note</option>
                      </select>
                    </div>
                    {customData.invoiceType === 'Debit Note' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Reason <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={customData.reason || ''}
                          onChange={(e) => setCustomData({ ...customData, reason: e.target.value })}
                          placeholder="Reason for Debit Note"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Scenario ID
                      </label>
                      <input
                        type="text"
                        value={customData.scenarioId || ''}
                        onChange={(e) => setCustomData({ ...customData, scenarioId: e.target.value })}
                        placeholder={scenarioDetails.id}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
                        readOnly
                      />
                    </div>
                  </div>
                </div>

                {/* Seller Information - HIDDEN */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200" style={{ display: 'none' }}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-green-800">Seller Information</h3>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      Auto-populated from your profile
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Seller NTN/CNIC
                      </label>
                      <input
                        type="text"
                        value={customData.sellerNTNCNIC || ''}
                        onChange={(e) => setCustomData({ ...customData, sellerNTNCNIC: e.target.value })}
                        placeholder="Enter seller NTN/CNIC"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-green-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Seller Business Name
                      </label>
                      <input
                        type="text"
                        value={customData.sellerBusinessName || ''}
                        onChange={(e) => setCustomData({ ...customData, sellerBusinessName: e.target.value })}
                        placeholder="Enter seller business name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-green-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Seller Province
                      </label>
                      <select
                        value={customData.sellerProvince}
                        onChange={(e) => setCustomData({ ...customData, sellerProvince: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-green-50"
                      >
                        {referenceData.province?.map(option => (
                          <option key={option.code} value={option.description}>{option.description}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Seller Address
                      </label>
                      <input
                        type="text"
                        value={customData.sellerAddress || ''}
                        onChange={(e) => setCustomData({ ...customData, sellerAddress: e.target.value })}
                        placeholder="Enter seller address"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-green-50"
                      />
                    </div>
                  </div>
                  {userProfile && (
                    <div className="mt-3 text-xs text-green-600">
                      📋’¡ This information is automatically loaded from your profile. You can edit it here for this invoice only.
                    </div>
                  )}
                </div>

                {/* Buyer Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <fieldset disabled={customData.invoiceType === 'Debit Note'} className={customData.invoiceType === 'Debit Note' ? 'opacity-50' : ''}>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-medium">Buyer Information</h3>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={selectWalkInCustomer}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                        >
                          👤 Walk In Customer
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowBuyerPopup(true)}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                        >
                          📋 Manage Buyers
                        </button>
                        <button
                          type="button"
                          onClick={clearBuyerFields}
                          className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                        >
                          📋 Clear
                        </button>
                      </div>
                    </div>

                    {/* Manual Fields (populated by buyer selection or manual entry) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Buyer NTN/CNIC
                        </label>
                        <input
                          type="text"
                          value={customData.buyerNTNCNIC || ''}
                          onChange={(e) => setCustomData({ ...customData, buyerNTNCNIC: e.target.value })}
                          placeholder="Enter buyer NTN/CNIC"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Buyer Business Name
                        </label>
                        <input
                          type="text"
                          value={customData.buyerBusinessName || ''}
                          onChange={(e) => setCustomData({ ...customData, buyerBusinessName: e.target.value })}
                          placeholder="Enter buyer business name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Buyer Province
                        </label>
                        <select
                          value={customData.buyerProvince || 'Punjab'}
                          onChange={(e) => setCustomData({ ...customData, buyerProvince: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {referenceData.province?.map(option => (
                            <option key={option.code} value={option.description}>{option.description}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Buyer Address
                        </label>
                        <input
                          type="text"
                          value={customData.buyerAddress || ''}
                          onChange={(e) => setCustomData({ ...customData, buyerAddress: e.target.value })}
                          placeholder="Enter buyer address"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Buyer Registration Type
                        </label>
                        <select
                          value={customData.buyerRegistrationType || 'Unregistered'}
                          onChange={(e) => setCustomData({ ...customData, buyerRegistrationType: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {referenceData.registration_type?.map(option => (
                            <option key={option.code} value={option.description}>{option.description}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Display current buyer info if filled */}
                    {(customData.buyerBusinessName || customData.buyerNTNCNIC) && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                        <div className="font-medium text-blue-800">Current Buyer:</div>
                        <div className="text-blue-700">
                          {customData.buyerBusinessName && <span>{customData.buyerBusinessName}</span>}
                          {customData.buyerBusinessName && customData.buyerNTNCNIC && <span> : </span>}
                          {customData.buyerNTNCNIC && <span>NTN/CNIC: {customData.buyerNTNCNIC}</span>}
                        </div>
                      </div>
                    )}
                  </fieldset>
                </div>



                {/* Items Management */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <fieldset disabled={customData.invoiceType === 'Debit Note'} className={customData.invoiceType === 'Debit Note' ? 'opacity-50' : ''}>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-medium">Invoice Items</h3>
                      <button
                        type="button"
                        onClick={() => {
                          const newItem = {
                            hsCode: '',
                            productDescription: '',
                            rate: '18%',
                            uoM: 'Numbers, pieces, units', // Default UoM for new items
                            quantity: 1,
                            singleUnitPrice: 0, // New field for user input
                            priceIncludesTax: ["SN008", "SN027"].includes(selectedScenario) ? true : false, // Force true for 3rd Schedule
                            unitPriceExcludingTax: 0, // For display in "Unit Price (Excl. Tax) - Calculated"
                            valueSalesExcludingST: 0,
                            salesTaxApplicable: 0, // Store calculated tax amount, not rate
                            totalValues: 0,
                            fixedNotifiedValueOrRetailPrice: 0,
                            salesTaxWithheldAtSource: 0,
                            extraTax: 0,
                            furtherTax: 0,
                            sroScheduleNo: '',
                            fedPayable: 0,
                            discount: 0,
                            saleType: customData.saleType,
                            sroItemSerialNo: ''
                          };
                          setCustomData({
                            ...customData,
                            customItems: [...customData.customItems, newItem]
                          });
                        }}
                        className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                      >
                        + Add Item
                      </button>
                    </div>

                    {customData.customItems.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>No custom items added. The scenario's default items will be used.</p>
                        <p className="text-sm mt-1">Click "Add Item" to customize invoice items.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {customData.customItems.map((item, index) => (
                          <div key={`item-${index}`} className="border border-gray-200 rounded-lg p-4 bg-white">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="font-medium text-sm">Item {index + 1}</h4>
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedItems = customData.customItems.filter((_, i) => i !== index);
                                  setCustomData({
                                    ...customData,
                                    customItems: updatedItems
                                  });
                                }}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Remove
                              </button>
                            </div>

                            {/* Dynamic Field Toggle */}
                            <div className="mb-4">
                              <DynamicFieldToggle
                                scenarioId={selectedScenario}
                                fieldConfig={fieldConfig}
                                visibleFields={visibleFields}
                                onFieldVisibilityChange={setVisibleFields}
                                className="w-full"
                              />
                            </div>

                            {/* Enhanced HS Code Search with UoM Validation */}
                            {/* HS Code + Valid UoM component stays visible */}
                            <div className="mb-4">
                              <HSCodeSearchWithUoM
                                onHSCodeSelect={(hsCodeData) => {
                                  const updatedItems = [...customData.customItems];
                                  updatedItems[index].hsCode = hsCodeData.code;
                                  updatedItems[index].productDescription = hsCodeData.description;
                                  updatedItems[index] = applyKnownSROCorrection(updatedItems[index], selectedScenario);
                                  setCustomData({ ...customData, customItems: updatedItems });
                                }}
                                onUoMChange={(newUoM) => {
                                  const updatedItems = [...customData.customItems];
                                  updatedItems[index].uoM = newUoM;
                                  setCustomData({ ...customData, customItems: updatedItems });
                                }}
                                currentHSCode={item.hsCode}
                                currentUoM={item.uoM}
                                showUoMValidation={true}
                              />
                            </div>











                            {/* Detailed grid (restored visible) */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div style={{ display: 'none' }}>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  HS Code
                                </label>
                                <input
                                  type="text"
                                  value={item.hsCode}
                                  onChange={(e) => {
                                    const updatedItems = [...customData.customItems];
                                    updatedItems[index].hsCode = e.target.value;
                                    updatedItems[index] = applyKnownSROCorrection(updatedItems[index], selectedScenario);
                                    setCustomData({ ...customData, customItems: updatedItems });
                                  }}
                                  placeholder="e.g., 0101.2100"
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-gray-50"
                                  readOnly
                                />
                                <div className="text-xs text-gray-500 mt-1">Auto-filled from HS Code search</div>
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Product Description
                                </label>
                                <input
                                  type="text"
                                  value={item.productDescription}
                                  onChange={(e) => {
                                    const updatedItems = [...customData.customItems];
                                    updatedItems[index].productDescription = e.target.value;
                                    setCustomData({ ...customData, customItems: updatedItems });
                                  }}
                                  placeholder="Enter product description"
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                <div className="text-xs text-gray-500 mt-1">Auto-filled from HS Code search (editable)</div>
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Quantity
                                </label>
                                <input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const updatedItems = [...customData.customItems];
                                    const quantity = parseInt(e.target.value) || 0;
                                    updatedItems[index].quantity = quantity;

                                    // Recalculate using singleUnitPrice if available
                                    const singleUnitPrice = updatedItems[index].singleUnitPrice || 0;
                                    const taxRate = parseFloat(updatedItems[index].rate.replace('%', ''));
                                    const priceIncludesTax = updatedItems[index].priceIncludesTax || false;

                                    if (singleUnitPrice > 0) {
                                      // Use new calculation method
                                      const calculatedValues = calculateItemValues(singleUnitPrice, quantity, taxRate, priceIncludesTax, selectedScenario);
                                      updatedItems[index].unitPriceExcludingTax = calculatedValues.unitPriceExcludingTax;
                                      updatedItems[index].valueSalesExcludingST = calculatedValues.valueSalesExcludingST;
                                      updatedItems[index].salesTaxApplicable = calculatedValues.salesTaxApplicable;
                                      updatedItems[index].totalValues = calculatedValues.totalValues;
                                      updatedItems[index].fixedNotifiedValueOrRetailPrice = calculatedValues.fixedNotifiedValueOrRetailPrice;

                                      // ✅ Auto-update furtherTax based on scenario
                                      const baseValue = calculatedValues.fixedNotifiedValueOrRetailPrice || calculatedValues.valueSalesExcludingST || 0;
                                      const furtherTax = calculateFurtherTax(baseValue, selectedScenario, customData.buyerRegistrationType);
                                      updatedItems[index].furtherTax = furtherTax;
                                      console.log(`📋  Quantity changed - Further Tax: ${furtherTax}, ${quantity}, Single Unit Price: ${singleUnitPrice}, Calculated:`, calculatedValues);
                                    }

                                    setCustomData({ ...customData, customItems: updatedItems });
                                  }}
                                  min="1"
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  placeholder="Enter quantity"
                                />
                              </div>

                              {/* Single Unit Price Input with Tax Inclusion Radio */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Single Unit Price
                                </label>
                                <input
                                  type="number"
                                  value={item.singleUnitPrice || 0}
                                  onChange={(e) => {
                                    const updatedItems = [...customData.customItems];
                                    const singleUnitPrice = parseFloat(e.target.value) || 0;
                                    updatedItems[index].singleUnitPrice = singleUnitPrice;

                                    // Calculate values based on tax inclusion
                                    const quantity = updatedItems[index].quantity || 1;
                                    const taxRate = parseFloat(updatedItems[index].rate.replace('%', ''));
                                    const priceIncludesTax = updatedItems[index].priceIncludesTax || false;

                                    const calculatedValues = calculateItemValues(singleUnitPrice, quantity, taxRate, priceIncludesTax, selectedScenario);

                                    updatedItems[index].unitPriceExcludingTax = calculatedValues.unitPriceExcludingTax;
                                    updatedItems[index].valueSalesExcludingST = calculatedValues.valueSalesExcludingST;
                                    updatedItems[index].salesTaxApplicable = calculatedValues.salesTaxApplicable;
                                    updatedItems[index].totalValues = calculatedValues.totalValues;
                                    updatedItems[index].fixedNotifiedValueOrRetailPrice = calculatedValues.fixedNotifiedValueOrRetailPrice;

                                    // ✅ Auto-update furtherTax based on scenario
                                    const baseValue = calculatedValues.fixedNotifiedValueOrRetailPrice || calculatedValues.valueSalesExcludingST || 0;
                                    const furtherTax = calculateFurtherTax(baseValue, selectedScenario, customData.buyerRegistrationType);
                                    updatedItems[index].furtherTax = furtherTax;
                                    console.log(`📋  Single Unit Price [Further Tax: ${furtherTax}]: ${singleUnitPrice}, Includes Tax: ${priceIncludesTax}, Calculated:`, calculatedValues);
                                    console.log(`📋  Updated Item [${index}]:`, updatedItems[index]);

                                    setCustomData({ ...customData, customItems: updatedItems });
                                  }}
                                  onKeyDown={(e) => {
                                    // Prevent Enter key from submitting form and causing page refresh
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                    }
                                    // Prevent arrow keys from incrementing/decrementing
                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                      e.preventDefault();
                                    }
                                  }}
                                  min="0"
                                  step="0.01"
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                  placeholder="Enter unit price"
                                />

                                {/* Radio buttons for tax inclusion */}
                                <div className="mt-2 flex gap-4">
                                  <label className={`flex items-center text-xs ${["SN008", "SN027"].includes(selectedScenario) ? 'text-gray-400 cursor-not-allowed' : ''}`}>
                                    <input
                                      type="radio"
                                      name={`priceType_${index}`}
                                      checked={!item.priceIncludesTax}
                                      disabled={["SN008", "SN027"].includes(selectedScenario)}
                                      onChange={() => {
                                        const updatedItems = [...customData.customItems];
                                        updatedItems[index].priceIncludesTax = false;

                                        // Recalculate values
                                        const singleUnitPrice = updatedItems[index].singleUnitPrice || 0;
                                        const quantity = updatedItems[index].quantity || 1;
                                        const taxRate = parseFloat(updatedItems[index].rate.replace('%', ''));

                                        const calculatedValues = calculateItemValues(singleUnitPrice, quantity, taxRate, false, selectedScenario);

                                        updatedItems[index].unitPriceExcludingTax = calculatedValues.unitPriceExcludingTax;
                                        updatedItems[index].valueSalesExcludingST = calculatedValues.valueSalesExcludingST;
                                        updatedItems[index].salesTaxApplicable = calculatedValues.salesTaxApplicable;
                                        updatedItems[index].totalValues = calculatedValues.totalValues;
                                        updatedItems[index].fixedNotifiedValueOrRetailPrice = calculatedValues.fixedNotifiedValueOrRetailPrice;

                                        // ✅ Recalculate furtherTax when price type changes
                                        const baseValue = calculatedValues.fixedNotifiedValueOrRetailPrice || calculatedValues.valueSalesExcludingST || 0;
                                        updatedItems[index].furtherTax = calculateFurtherTax(baseValue, selectedScenario, customData.buyerRegistrationType);

                                        setCustomData({ ...customData, customItems: updatedItems });
                                      }}
                                      className="mr-1"
                                    />
                                    {["SN008", "SN027"].includes(selectedScenario) ? 'MRP Excluding Tax (Disabled for 3rd)' : 'Price Excluding Tax'}
                                  </label>
                                  <label className="flex items-center text-xs">
                                    <input
                                      type="radio"
                                      name={`priceType_${index}`}
                                      checked={item.priceIncludesTax}
                                      onChange={() => {
                                        const updatedItems = [...customData.customItems];
                                        updatedItems[index].priceIncludesTax = true;

                                        // Recalculate values
                                        const singleUnitPrice = updatedItems[index].singleUnitPrice || 0;
                                        const quantity = updatedItems[index].quantity || 1;
                                        const taxRate = parseFloat(updatedItems[index].rate.replace('%', ''));

                                        const calculatedValues = calculateItemValues(singleUnitPrice, quantity, taxRate, true, selectedScenario);

                                        updatedItems[index].unitPriceExcludingTax = calculatedValues.unitPriceExcludingTax;
                                        updatedItems[index].valueSalesExcludingST = calculatedValues.valueSalesExcludingST;
                                        updatedItems[index].salesTaxApplicable = calculatedValues.salesTaxApplicable;
                                        updatedItems[index].totalValues = calculatedValues.totalValues;
                                        updatedItems[index].fixedNotifiedValueOrRetailPrice = calculatedValues.fixedNotifiedValueOrRetailPrice;

                                        // ✅ Recalculate furtherTax when price type changes
                                        const baseValue = calculatedValues.fixedNotifiedValueOrRetailPrice || calculatedValues.valueSalesExcludingST || 0;
                                        updatedItems[index].furtherTax = calculateFurtherTax(baseValue, selectedScenario, customData.buyerRegistrationType);

                                        setCustomData({ ...customData, customItems: updatedItems });
                                      }}
                                      className="mr-1"
                                    />
                                    {["SN008", "SN027"].includes(selectedScenario) ? 'MRP Including Tax (Required for 3rd)' : 'Price Including Tax'}
                                  </label>
                                </div>

                                {/* Information message for 3rd Schedule scenarios */}
                                {["SN008", "SN027"].includes(selectedScenario) && (
                                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                                    <strong>3rd Schedule Goods:</strong> Enter the Maximum Retail Price (MRP). Tax will be calculated on MRP × quantity, not transaction value.
                                  </div>
                                )}


                              </div>

                              {/* UoM Field */}
                              {visibleFields.uoM && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    UoM***
                                    {fieldConfig.uoM?.required && <span className="text-red-500 ml-1">*</span>}
                                  </label>
                                  <select
                                    value={item.uoM}
                                    onChange={(e) => {
                                      const updatedItems = [...customData.customItems];
                                      updatedItems[index].uoM = e.target.value;
                                      setCustomData({ ...customData, customItems: updatedItems });
                                    }}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  >
                                    {referenceData.uom?.map(option => (
                                      <option key={option.code} value={option.description}>{option.description}</option>
                                    ))}
                                  </select>
                                  {fieldConfig.uoM?.defaultValue && (
                                    <div className="text-xs text-blue-600 mt-1">
                                      Scenario default: {fieldConfig.uoM.defaultValue}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Tax Rate Input - Editable */}
                              {visibleFields.rate && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Tax Rate**
                                    {fieldConfig.rate?.required && <span className="text-red-500 ml-1">*</span>}
                                  </label>
                                  <input
                                    type="number"
                                    value={parseFloat(item.rate.replace('%', '')) || 0}
                                    onChange={(e) => {
                                      const updatedItems = [...customData.customItems];
                                      const taxRateValue = parseFloat(e.target.value) || 0;
                                      updatedItems[index].rate = `${taxRateValue}%`;

                                      // Recalculate all values when tax rate changes
                                      const singleUnitPrice = updatedItems[index].singleUnitPrice || 0;
                                      const quantity = updatedItems[index].quantity || 1;
                                      const priceIncludesTax = updatedItems[index].priceIncludesTax || false;

                                      if (singleUnitPrice > 0) {
                                        const calculatedValues = calculateItemValues(singleUnitPrice, quantity, taxRateValue, priceIncludesTax, selectedScenario);
                                        updatedItems[index].unitPriceExcludingTax = calculatedValues.unitPriceExcludingTax;
                                        updatedItems[index].valueSalesExcludingST = calculatedValues.valueSalesExcludingST;
                                        updatedItems[index].salesTaxApplicable = calculatedValues.salesTaxApplicable;
                                        updatedItems[index].totalValues = calculatedValues.totalValues;
                                        updatedItems[index].fixedNotifiedValueOrRetailPrice = calculatedValues.fixedNotifiedValueOrRetailPrice;

                                        // ✅ Recalculate furtherTax when tax rate changes
                                        const baseValue = calculatedValues.fixedNotifiedValueOrRetailPrice || calculatedValues.valueSalesExcludingST || 0;
                                        updatedItems[index].furtherTax = calculateFurtherTax(baseValue, selectedScenario, customData.buyerRegistrationType);

                                        console.log(`📋  Tax Rate changed: ${taxRateValue}%, Further Tax: ${updatedItems[index].furtherTax}, Calculated:`, calculatedValues);
                                      }

                                      updatedItems[index] = applyKnownSROCorrection(updatedItems[index], selectedScenario);
                                      setCustomData({ ...customData, customItems: updatedItems });
                                    }}
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="Enter tax rate (e.g., 18)"
                                  />
                                  <div className="text-xs text-gray-500 mt-1">Enter tax rate as number (e.g., 18 for 18%)</div>
                                  {fieldConfig.rate?.defaultValue && (
                                    <div className="text-xs text-blue-600 mt-1">
                                      Scenario default: {fieldConfig.rate.defaultValue}
                                    </div>
                                  )}
                                </div>
                              )}


                              {/* Value Sales Excluding ST (Total) */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Value Sales Excluding ST (Total)
                                </label>
                                <input
                                  type="number"
                                  value={roundToTwoDecimals(item.valueSalesExcludingST || 0)}
                                  readOnly
                                  key={`valueSalesExcludingST-${index}-${item.valueSalesExcludingST}`}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                                />
                              </div>

                              {/* Fixed Notified Value for 3rd Schedule scenarios */}
                              {["SN008", "SN027"].includes(selectedScenario) && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Fixed Notified Value / MRP (Total)
                                  </label>
                                  <input
                                    type="number"
                                    value={roundToTwoDecimals(item.fixedNotifiedValueOrRetailPrice || 0)}
                                    readOnly
                                    key={`fixedNotifiedValue-${index}-${item.fixedNotifiedValueOrRetailPrice}`}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-blue-50"
                                  />
                                </div>
                              )}

                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Sales Tax
                                </label>
                                {(() => {
                                  const salesTaxFormatted = formatSalesTax(item.salesTaxApplicable || 0);
                                  return (
                                    <input
                                      type="text"
                                      value={salesTaxFormatted.formatted}
                                      readOnly
                                      key={`salesTax-${index}-${item.salesTaxApplicable}`}
                                      className={`w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100 ${salesTaxFormatted.className}`}
                                    />
                                  );
                                })()}
                              </div>

                              {visibleFields.saleType && (
                                <div style={{ display: 'none' }}>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Sale Type
                                    <span className="text-xs text-gray-500 ml-1">(Auto-selected based on scenario)</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={item.saleType}
                                    readOnly
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                                    title="Sale Type is automatically selected based on the scenario and cannot be changed"
                                  />
                                  <div className="text-xs text-gray-500 mt-1">
                                    Automatically set by scenario - cannot be modified
                                  </div>
                                </div>
                              )}

                              {/* Unit Price (Excl. Tax) - Calculated - Now moved to UoM position */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Unit Price (Excl. Tax) - Calculated
                                </label>
                                <input
                                  type="number"
                                  value={roundToTwoDecimals(item.unitPriceExcludingTax || 0)}
                                  readOnly
                                  key={`unitPrice-${index}-${item.unitPriceExcludingTax}`}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                                  // This field is now readonly and calculated automatically
                                  min="0"
                                  step="1"
                                  onKeyDown={(e) => {
                                    // Prevent arrow keys from incrementing/decrementing
                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                      e.preventDefault();
                                    }
                                  }}
                                  onWheel={(e) => {
                                    // Prevent mouse wheel from changing value
                                    e.target.blur();
                                  }}

                                />
                              </div>

                              {/* Additional Dynamic Fields */}
                              {visibleFields.salesTaxWithheldAtSource && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Sales Tax Withheld at Source
                                  </label>
                                  <input
                                    type="number"
                                    value={item.salesTaxWithheldAtSource || 0}
                                    onChange={(e) => {
                                      const updatedItems = [...customData.customItems];
                                      updatedItems[index].salesTaxWithheldAtSource = parseFloat(e.target.value) || 0;
                                      setCustomData({ ...customData, customItems: updatedItems });
                                    }}
                                    min="0"
                                    step="0.01"
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                </div>
                              )}

                              {visibleFields.extraTax && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Extra Tax
                                  </label>
                                  <input
                                    type="number"
                                    value={item.extraTax || 0}
                                    onChange={(e) => {
                                      const updatedItems = [...customData.customItems];
                                      updatedItems[index].extraTax = parseFloat(e.target.value) || 0;
                                      setCustomData({ ...customData, customItems: updatedItems });
                                    }}
                                    min="0"
                                    step="0.01"
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                </div>
                              )}

                              {visibleFields.furtherTax && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Further Tax
                                  </label>
                                  <input
                                    type="number"
                                    value={item.furtherTax || 0}
                                    onChange={(e) => {
                                      const updatedItems = [...customData.customItems];
                                      updatedItems[index].furtherTax = parseFloat(e.target.value) || 0;
                                      setCustomData({ ...customData, customItems: updatedItems });
                                    }}
                                    min="0"
                                    step="0.01"
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                </div>
                              )}

                              {visibleFields.fixedNotifiedValueOrRetailPrice && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Fixed Notified Value / Retail Price
                                    {["SN008", "SN027"].includes(selectedScenario) ? (
                                      <span className="text-xs text-gray-600 ml-1">(Unit Price × Quantity - Tax calculated on this value)</span>
                                    ) : (
                                      <span className="text-xs text-orange-600 ml-1">(Not used in {selectedScenario} - Always 0)</span>
                                    )}
                                    {selectedScenario === 'SN008' && (
                                      <span className="text-xs text-red-600 ml-1 font-semibold">[SN008]</span>
                                    )}
                                    {selectedScenario === 'SN027' && (
                                      <span className="text-xs text-blue-600 ml-1 font-semibold">[SN027]</span>
                                    )}
                                  </label>
                                  <input
                                    type="number"
                                    value={item.fixedNotifiedValueOrRetailPrice || 0}
                                    disabled={["SN008", "SN027"].includes(selectedScenario)}
                                    onChange={(e) => {
                                      const updatedItems = [...customData.customItems];
                                      // For non-3rd Schedule scenarios only; for SN008/SN027 we auto-calc from singleUnitPrice × quantity
                                      let fixedValue = parseFloat(e.target.value) || 0;

                                      if (["SN008", "SN027"].includes(selectedScenario)) {
                                        const singleUnitPrice = updatedItems[index].singleUnitPrice || 0;
                                        const quantity = updatedItems[index].quantity || 1;
                                        fixedValue = roundToTwoDecimals(singleUnitPrice * quantity);
                                        console.log(`📋  ${selectedScenario}: auto-calculated fixedNotifiedValueOrRetailPrice = unitPrice(${singleUnitPrice}) × qty(${quantity}) = ${fixedValue}`);
                                      }

                                      updatedItems[index].fixedNotifiedValueOrRetailPrice = fixedValue;

                                      // Handle Fixed Notified Value changes based on scenario requirements
                                      const taxRate = parseFloat(updatedItems[index].rate.replace('%', '')); // Keep as percentage
                                      const quantity = updatedItems[index].quantity || 1;

                                      if (["SN008", "SN027"].includes(selectedScenario)) {
                                        // Auto-calc fixed value from singleUnitPrice × quantity
                                        const singleUnitPrice = updatedItems[index].singleUnitPrice || 0;
                                        const autoFixed = roundToTwoDecimals(singleUnitPrice * quantity);
                                        updatedItems[index].fixedNotifiedValueOrRetailPrice = autoFixed;
                                        // For 3rd Schedule: valueSalesExcludingST = 0 (MRP-based)
                                        updatedItems[index].valueSalesExcludingST = 0;
                                        // Calculate salesTaxApplicable on fixed value (two decimals)
                                        const itemForCalculation = {
                                          fixedNotifiedValueOrRetailPrice: autoFixed,
                                          quantity: 1,
                                          saleType: '3rd Schedule Goods'
                                        };
                                        updatedItems[index].salesTaxApplicable = calculateSalesTaxByScenario(itemForCalculation, taxRate, selectedScenario);
                                        // Total Values = Fixed Value (per working sample behavior)
                                        updatedItems[index].totalValues = roundToTwoDecimals(autoFixed);
                                      } else {
                                        // For all other scenarios: Fixed Notified Value should be 0, ignore user input
                                        updatedItems[index].fixedNotifiedValueOrRetailPrice = 0;
                                        // Calculate salesTaxApplicable as tax amount using scenario-based calculation
                                        const totalSaleValue = updatedItems[index].valueSalesExcludingST * quantity;
                                        const itemForCalculation = {
                                          valueSalesExcludingST: updatedItems[index].valueSalesExcludingST,
                                          quantity: quantity,
                                          saleType: 'Goods at standard rate (default)'
                                        };
                                        updatedItems[index].salesTaxApplicable = calculateSalesTaxByScenario(itemForCalculation, taxRate, selectedScenario);
                                        // Total Values = Sale Value + calculated tax
                                        updatedItems[index].totalValues = roundToTwoDecimals(totalSaleValue + updatedItems[index].salesTaxApplicable);
                                      }
                                      console.log(`📋  ${selectedScenario}: Fixed Value changed to ${fixedValue}, Unit Price: ${updatedItems[index].valueSalesExcludingST}, Quantity: ${quantity}, Tax: ${updatedItems[index].salesTaxApplicable}, Total: ${updatedItems[index].totalValues}`);

                                      setCustomData({ ...customData, customItems: updatedItems });
                                    }}
                                    min="0"
                                    step="0.01"
                                    onKeyDown={(e) => {
                                      // Prevent arrow keys from incrementing/decrementing
                                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                        e.preventDefault();
                                      }
                                    }}
                                    onWheel={(e) => {
                                      // Prevent mouse wheel from changing value
                                      e.target.blur();
                                    }}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 ${!["SN008", "SN027"].includes(selectedScenario)
                                      ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                                      : selectedScenario === 'SN008'
                                        ? 'border-red-300 focus:ring-red-500 bg-red-50'
                                        : selectedScenario === 'SN027'
                                          ? 'border-blue-300 focus:ring-blue-500 bg-blue-50'
                                          : 'border-gray-300 focus:ring-blue-500'
                                      }`}
                                    placeholder={selectedScenario === 'SN008' ? "Enter value (e.g., 1005)" : "Enter retail price or notified value"}
                                  />
                                  <p className="text-xs text-gray-600 mt-1">
                                    This value = Unit Price × Quantity. Sales tax calculated on this total value.
                                    {selectedScenario === 'SN008' && <span className="text-red-600 font-semibold"> [SN008 Special]</span>}
                                    {selectedScenario === 'SN027' && <span className="text-blue-600 font-semibold"> [SN027 Special]</span>}
                                  </p>
                                </div>
                              )}

                              {visibleFields.sroScheduleNo && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    SRO Schedule No
                                  </label>
                                  <input
                                    type="text"
                                    value={item.sroScheduleNo || ''}
                                    disabled
                                    readOnly
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                                    placeholder="e.g., EIGHTH SCHEDULE Table 1"
                                  />
                                </div>
                              )}

                              {visibleFields.fedPayable && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    FED Payable
                                  </label>
                                  <input
                                    type="number"
                                    value={item.fedPayable || 0}
                                    onChange={(e) => {
                                      const updatedItems = [...customData.customItems];
                                      updatedItems[index].fedPayable = parseFloat(e.target.value) || 0;
                                      setCustomData({ ...customData, customItems: updatedItems });
                                    }}
                                    min="0"
                                    step="0.01"
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                </div>
                              )}

                              {visibleFields.discount && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Discount
                                  </label>
                                  <input
                                    type="number"
                                    value={item.discount || 0}
                                    onChange={(e) => {
                                      const updatedItems = [...customData.customItems];
                                      updatedItems[index].discount = parseFloat(e.target.value) || 0;
                                      setCustomData({ ...customData, customItems: updatedItems });
                                    }}
                                    min="0"
                                    step="0.01"
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                </div>
                              )}

                              {visibleFields.sroItemSerialNo && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    SRO Item Serial No
                                  </label>
                                  <input
                                    type="text"
                                    value={item.sroItemSerialNo || ''}
                                    disabled
                                    readOnly
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100 cursor-not-allowed"
                                    placeholder="e.g., 70"
                                  />
                                </div>
                              )}

                              {/* Total Value moved next to SRO Item Serial No */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Total Value</label>
                                <input
                                  type="number"
                                  value={roundToTwoDecimals(item.totalValues || 0)}
                                  readOnly
                                  key={`totalValues-${index}-${item.totalValues}`}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded bg-gray-100"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </fieldset>
                </div>

                {/* Invoice Note - Moved under Invoice Items */}
                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-yellow-800 text-sm">Invoice Note</h3>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-gray-600">Font: {noteFontSize}px</span>
                      <button
                        type="button"
                        onClick={() => setNoteFontSize(size => Math.max(MIN_NOTE_FONT_SIZE, size - 1))}
                        className="px-2 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50"
                      >
                        -
                      </button>
                      <button
                        type="button"
                        onClick={() => setNoteFontSize(size => Math.min(MAX_NOTE_FONT_SIZE, size + 1))}
                        className="px-2 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Note (Optional)
                    </label>
                    <textarea
                      value={customData.note || ''}
                      onChange={(e) => setCustomData({ ...customData, note: e.target.value })}
                      placeholder="Add any additional notes for this invoice... (Press Enter for new lines)"
                      rows={2}
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                      style={{ minHeight: '60px', maxHeight: '120px', fontSize: `${noteFontSize}px` }}
                    />
                    <div className="mt-1 text-xs text-gray-500">
                      📋’¡ This note supports multiple lines and will be displayed on the invoice but won't be sent to FBR.
                    </div>
                  </div>
                </div>

                {/* Scenario Preview */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">Scenario Details</h3>
                  <div className="text-sm space-y-2">
                    <p><strong>Scenario ID:</strong> {scenarioDetails.id}</p>
                    <p><strong>Name:</strong> {scenarioDetails.name}</p>
                    <p><strong>Description:</strong> {scenarioDetails.description}</p>
                    {scenarioDetails.defaultData.items && scenarioDetails.defaultData.items[0] && (
                      <div className="mt-3">
                        <p><strong>Default Item:</strong></p>
                        <ul className="ml-4 space-y-1">
                          <li>HS Code: {scenarioDetails.defaultData.items[0].hsCode}</li>
                          <li>Product: {scenarioDetails.defaultData.items[0].productDescription}</li>
                          <li>Tax Rate: {scenarioDetails.defaultData.items[0].rate}</li>
                          <li>Sale Type: {scenarioDetails.defaultData.items[0].saleType}</li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Preview JSON Data Button - HIDDEN */}

                <button
                  onClick={generateInvoice}
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Generating...' : 'Generate Invoice JSON'}
                </button>
              </div>
            </div>
          )}

          {/* Preview Invoice Tab */}
          {activeTab === 'preview' && generatedInvoice && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  Invoice Preview - {generatedInvoice.scenarioName}
                </h2>
                <div className="space-x-2">
                  {/* Show JSON Data Button - HIDDEN */}
                  <button
                    onClick={() => downloadJSON(generatedInvoice.invoiceData, `invoice-${generatedInvoice.scenarioId}-${Date.now()}.json`)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Download JSON
                  </button>
                  <button
                    onClick={validateInvoice}
                    disabled={loading || !licenseStatus?.canSubmitInvoices}
                    className={`px-4 py-2 rounded-lg transition-colors ${!licenseStatus?.canSubmitInvoices
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
                      }`}
                    title={!licenseStatus?.canSubmitInvoices ? 'License required for FBR validation' : ''}
                  >
                    {loading ? 'Validating...' : 'Validate Invoice'}
                  </button>
                </div>
              </div>

              {/* JSON Data Display - HIDDEN */}

              {/* Formatted Invoice Display */}
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                <div
                  ref={invoiceRef}
                  className="bg-white border border-gray-300 p-8 max-w-4xl mx-auto"
                  style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px', lineHeight: '1.4' }}
                >
                  {/* FBR Digital Invoicing System Header */}
                  <FBRInvoiceHeader
                    invoiceNumber={generatedInvoice.invoiceData.invoiceRefNo}
                    fbrInvoiceNumber={generatedInvoice.fbrInvoiceNumber}
                    status={generatedInvoice.fbrInvoiceNumber ? "compliant" : "pending"}
                    className="mb-6"
                    invoiceData={{
                      ...generatedInvoice.invoiceData,
                      fbr_invoice_number: generatedInvoice.fbrInvoiceNumber,
                      invoice_number: generatedInvoice.fbrInvoiceNumber || generatedInvoice.invoiceData.invoiceRefNo,
                      invoice_date: generatedInvoice.invoiceData.invoiceDate,
                      seller_ntn_cnic: generatedInvoice.invoiceData.sellerNTNCNIC,
                      seller_business_name: generatedInvoice.invoiceData.sellerBusinessName,
                      buyer_ntn_cnic: generatedInvoice.invoiceData.buyerNTNCNIC,
                      buyer_business_name: generatedInvoice.invoiceData.buyerBusinessName,
                      total_amount: generatedInvoice.invoiceData.items?.reduce((sum, item) => sum + (item.totalValues || 0), 0) || 0,
                      sales_tax: generatedInvoice.invoiceData.items?.reduce((sum, item) => {
                        // salesTaxApplicable now contains the calculated tax amount, not the rate
                        const calculatedTax = item.salesTaxApplicable || 0;
                        return sum + calculatedTax;
                      }, 0) || 0
                    }}
                    showQRCode={!!generatedInvoice.fbrInvoiceNumber}
                  />

                  {/* Invoice Header */}
                  <div className="flex justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-bold text-blue-600 mb-2">
                        {generatedInvoice.invoiceData.sellerBusinessName || 'Business Name'}
                      </h2>
                      <div className="text-sm space-y-1">
                        <div><strong>NTN/CNIC:</strong> {generatedInvoice.invoiceData.sellerNTNCNIC}</div>
                        <div><strong>Address:</strong> {generatedInvoice.invoiceData.sellerAddress}</div>
                        <div><strong>Province:</strong> {generatedInvoice.invoiceData.sellerProvince}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <h1 className="text-xl font-bold text-blue-600 mb-2">
                        Invoice {generatedInvoice.fbrInvoiceNumber || generatedInvoice.invoiceData.invoiceRefNo}
                      </h1>
                      <div className="text-sm space-y-1">
                        <div><strong>Invoice Date:</strong> {formatInvoiceDate(generatedInvoice.invoiceData.invoiceDate)}</div>
                        <div><strong>Scenario:</strong> {generatedInvoice.scenarioId}</div>
                        <div><strong>Type:</strong> {generatedInvoice.invoiceData.invoiceType}</div>
                        {generatedInvoice.fbrInvoiceNumber && (
                          <div><strong>FBR Invoice Number:</strong> {generatedInvoice.fbrInvoiceNumber}</div>
                        )}
                      </div>
                      <div className={`mt-2 text-white text-xs px-2 py-1 inline-block ${generatedInvoice.fbrInvoiceNumber ? 'bg-green-600' : 'bg-blue-600'
                        }`}>
                        {generatedInvoice.fbrInvoiceNumber ? 'SUBMITTED TO FBR' : 'READY FOR FBR SUBMISSION'}
                      </div>
                    </div>
                  </div>

                  {/* Buyer Information */}
                  <div className="mb-6 p-4 bg-gray-50 rounded">
                    <h3 className="font-bold mb-2">Bill To:</h3>
                    <div className="text-sm space-y-1">
                      <div><strong>Business Name:</strong> {generatedInvoice.invoiceData.buyerBusinessName}</div>
                      <div><strong>NTN/CNIC:</strong> {generatedInvoice.invoiceData.buyerNTNCNIC}</div>
                      <div><strong>Address:</strong> {generatedInvoice.invoiceData.buyerAddress}</div>
                      <div><strong>Province:</strong> {generatedInvoice.invoiceData.buyerProvince}</div>
                      <div><strong>Registration Type:</strong> {generatedInvoice.invoiceData.buyerRegistrationType}</div>
                    </div>
                  </div>

                  {/* Debit Note Reason */}
                  {customData.invoiceType === 'Debit Note' && customData.reason && (
                    <div className="mb-6 p-3 bg-gray-50 border border-gray-200 rounded">
                      <p className="text-xs text-gray-500 uppercase font-bold">Reason for Debit Note</p>
                      <p className="text-sm text-gray-800">{customData.reason}</p>
                    </div>
                  )}

                  {/* Items Table */}
                  {generatedInvoice.invoiceData.items && generatedInvoice.invoiceData.items.length > 0 && (
                    <div className="mb-6">
                      <div className="text-right mb-2 text-sm">Amount in Pakistan Rupee currency</div>
                      <table className="w-full border-collapse border border-gray-300 text-xs">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border border-gray-300 p-2 text-left">Description</th>
                            <th className="border border-gray-300 p-2">HS Code</th>
                            <th className="border border-gray-300 p-2">Rate</th>
                            <th className="border border-gray-300 p-2">Qty</th>
                            <th className="border border-gray-300 p-2">UoM</th>
                            <th className="border border-gray-300 p-2">Value (excl.)</th>
                            <th className="border border-gray-300 p-2">Sales Tax</th>
                            <th className="border border-gray-300 p-2">Further Tax</th>
                            <th className="border border-gray-300 p-2">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {generatedInvoice.invoiceData.items.map((item, index) => {
                            const salesTaxFormatted = formatSalesTax(item.salesTaxApplicable);
                            return (
                              <tr key={index}>
                                <td className="border border-gray-300 p-2">{item.productDescription}</td>
                                <td className="border border-gray-300 p-2 text-center">{item.hsCode}</td>
                                <td className="border border-gray-300 p-2 text-center">{item.rate}</td>
                                <td className="border border-gray-300 p-2 text-center">{item.quantity}</td>
                                <td className="border border-gray-300 p-2 text-center">{item.uoM}</td>
                                <td className="border border-gray-300 p-2 text-right">{formatCurrency(item.valueSalesExcludingST)}</td>
                                <td className={`border border-gray-300 p-2 text-right ${salesTaxFormatted.className}`}>
                                  {salesTaxFormatted.formatted}
                                </td>
                                <td className="border border-gray-300 p-2 text-right">{formatCurrency(item.furtherTax || 0)}</td>
                                <td className="border border-gray-300 p-2 text-right">{formatCurrency(item.totalValues)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Summary */}
                  <div className="flex justify-between">
                    {/* Note Section - Left Side */}
                    <div className="w-1/2 pr-4">
                      {customData.note && (
                        <div className="border-t border-gray-300 pt-4">
                          <h4 className="font-medium text-sm mb-2">Note:</h4>
                          <div
                            className="text-gray-700 bg-yellow-50 p-3 rounded border border-yellow-200 whitespace-pre-wrap"
                            style={{ fontSize: `${noteFontSize}px` }}
                          >
                            {customData.note}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Totals Section - Right Side */}
                    <div className="w-64">
                      <div className="border-t border-gray-300 pt-4">
                        {generatedInvoice.invoiceData.items && generatedInvoice.invoiceData.items.length > 0 && (
                          <>
                            <div className="flex justify-between text-sm mb-1">
                              <span>Subtotal:</span>
                              <span>{formatCurrency(generatedInvoice.invoiceData.items.reduce((sum, item) => sum + (item.valueSalesExcludingST || 0), 0))}</span>
                            </div>
                            <div className="flex justify-between text-sm mb-1">
                              <span>Sales Tax:</span>
                              {(() => {
                                const totalSalesTax = generatedInvoice.invoiceData.items.reduce((sum, item) => {
                                  // salesTaxApplicable now contains the calculated tax amount, not the rate
                                  const calculatedTax = item.salesTaxApplicable || 0;
                                  return sum + calculatedTax;
                                }, 0);
                                const salesTaxFormatted = formatSalesTax(totalSalesTax);
                                return (
                                  <span className={salesTaxFormatted.className}>
                                    {salesTaxFormatted.formatted}
                                  </span>
                                );
                              })()}
                            </div>
                            <div className="flex justify-between text-sm mb-1">
                              <span>Further Tax:</span>
                              <span>{formatCurrency(generatedInvoice.invoiceData.items.reduce((sum, item) => sum + (item.furtherTax || 0), 0))}</span>
                            </div>
                            <div className="flex justify-between font-bold text-lg border-t border-gray-300 pt-2">
                              <span>Total:</span>
                              <span>{formatCurrency(generatedInvoice.invoiceData.items.reduce((sum, item) => sum + (item.totalValues || 0), 0))}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="mt-8 pt-4 border-t border-gray-300 text-xs text-gray-600">
                    <p className="text-center font-semibold text-gray-700 mb-3">
                      This is Computer generated invoice did not required signature or stamp
                    </p>
                    <div className="flex justify-between">
                      <div>
                        <p>Generated on: {new Date().toLocaleString()}</p>
                        <p>Scenario: {generatedInvoice.scenarioName}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">Ready for FBR Validation</p>
                        <p>Click "Validate Invoice" to proceed</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Validate Tab */}
          {activeTab === 'validate' && validationResult && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">FBR Validation Result</h2>
                {validationResult.validation?.success && (
                  <button
                    onClick={submitInvoice}
                    disabled={loading || !licenseStatus?.canSubmitInvoices}
                    className={`px-4 py-2 rounded-lg transition-colors ${!licenseStatus?.canSubmitInvoices
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50'
                      }`}
                    title={!licenseStatus?.canSubmitInvoices ? 'License required for FBR submission' : ''}
                  >
                    {loading ? 'Submitting...' : 'Submit to FBR'}
                  </button>
                )}
              </div>

              <div className={`p-4 rounded-lg ${validationResult.validation?.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center mb-3">
                  <div className={`flex-shrink-0 ${validationResult.validation?.success ? 'text-green-400' : 'text-red-400'}`}>
                    {validationResult.validation?.success ? (
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <h3 className={`text-sm font-medium ${validationResult.validation?.success ? 'text-green-800' : 'text-red-800'}`}>
                      {validationResult.validation?.success ? 'Validation Successful' : 'Validation Failed'}
                    </h3>
                  </div>
                </div>

                {/* Success Message */}
                {validationResult.validation?.success ? (
                  <div className="space-y-3">
                    <div className="text-green-700">
                      <p className="font-medium text-lg">✅ Validation Successful!</p>
                      <p className="text-sm mt-1">Your invoice has passed all FBR validation checks and is ready for submission.</p>
                    </div>

                    {/* Show validation details if available */}
                    {validationResult.validation.validationResponse && (
                      <div className="bg-green-100 p-3 rounded border border-green-200">
                        <p className="text-sm text-green-800">
                          <strong>Status:</strong> {validationResult.validation.validationResponse.status || 'Valid'}
                        </p>
                        {validationResult.validation.validationResponse.statusCode && (
                          <p className="text-sm text-green-800">
                            <strong>Status Code:</strong> {validationResult.validation.validationResponse.statusCode}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Show UoM corrections if any */}
                    {validationResult.validation.uomCorrections && (
                      <div className="bg-blue-100 p-3 rounded border border-blue-200">
                        <p className="text-sm text-blue-800">
                          <strong>Auto-Corrections Made:</strong> {validationResult.validation.uomCorrections.message}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Error Messages */
                  <div className="space-y-3">
                    <div className="text-red-700">
                      <p className="font-medium text-lg">âŒ Validation Failed</p>
                      <p className="text-sm mt-1">Please review and fix the following issues:</p>
                    </div>

                    {/* Show main error message */}
                    {validationResult.validation?.error && (
                      <div className="bg-red-100 p-3 rounded border border-red-200">
                        <p className="text-sm text-red-800 font-medium">Error:</p>
                        <p className="text-sm text-red-800">{validationResult.validation.error}</p>
                      </div>
                    )}

                    {/* Show FBR validation response errors */}
                    {validationResult.validation?.validationResponse && (
                      <div className="bg-red-100 p-3 rounded border border-red-200">
                        <p className="text-sm text-red-800 font-medium">FBR Response:</p>
                        <p className="text-sm text-red-800">
                          <strong>Status:</strong> {validationResult.validation.validationResponse.status || 'Invalid'}
                        </p>
                        {validationResult.validation.validationResponse.statusCode && (
                          <p className="text-sm text-red-800">
                            <strong>Status Code:</strong> {validationResult.validation.validationResponse.statusCode}
                          </p>
                        )}
                        {validationResult.validation.validationResponse.errorCode && (
                          <p className="text-sm text-red-800">
                            <strong>Error Code:</strong> {validationResult.validation.validationResponse.errorCode}
                          </p>
                        )}
                        {validationResult.validation.validationResponse.error && (
                          <p className="text-sm text-red-800">
                            <strong>Error:</strong> {validationResult.validation.validationResponse.error}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Show item-specific errors */}
                    {validationResult.validation?.validationResponse?.invoiceStatuses &&
                      validationResult.validation.validationResponse.invoiceStatuses.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm text-red-800 font-medium">Item Errors:</p>
                          {validationResult.validation.validationResponse.invoiceStatuses.map((itemError, index) => (
                            <div key={index} className="bg-red-50 p-3 rounded border border-red-200">
                              <p className="text-sm text-red-800">
                                <strong>Item {itemError.itemSNo}:</strong> {itemError.error}
                              </p>
                              {itemError.errorCode && (
                                <p className="text-xs text-red-600 mt-1">
                                  Error Code: {itemError.errorCode}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                    {/* Show raw response for debugging if needed */}
                    <details className="mt-4">
                      <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                        Show Technical Details
                      </summary>
                      <div className="mt-2 bg-gray-100 p-3 rounded border">
                        <pre className="text-xs text-gray-700 overflow-auto max-h-32">
                          {JSON.stringify(validationResult.validation, null, 2)}
                        </pre>
                      </div>
                    </details>
                  </div>
                )}
              </div>
            </div>
          )}


          {/* Submit Tab */}
          {activeTab === 'submit' && submissionResult && (
            <div>
              <h2 className="text-lg font-semibold mb-4">FBR Submission Result</h2>

              <div className={`p-4 rounded-lg ${submissionResult.submission?.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center mb-3">
                  <div className={`flex-shrink-0 ${submissionResult.submission?.success ? 'text-green-400' : 'text-red-400'}`}>
                    {submissionResult.submission?.success ? (
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <h3 className={`text-sm font-medium ${submissionResult.submission?.success ? 'text-green-800' : 'text-red-800'}`}>
                      {submissionResult.submission?.success ? 'Submission Successful' : 'Submission Failed'}
                    </h3>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Success Message */}
                  {submissionResult.submission?.success ? (
                    <div className="space-y-3">
                      <div className="text-green-700">
                        <p className="font-medium text-lg">✅ Submission Successful!</p>
                        <p className="text-sm mt-1">Your invoice has been successfully submitted to FBR.</p>
                      </div>

                      {/* Show FBR Invoice Number */}
                      {submissionResult.fbrInvoiceNumber && (
                        <div className="bg-green-100 p-4 rounded border border-green-200">
                          <p className="text-sm text-green-800 font-medium">FBR Invoice Number Assigned:</p>
                          <p className="text-lg font-mono font-bold text-green-900 mt-1">
                            {submissionResult.fbrInvoiceNumber}
                          </p>
                          <p className="text-xs text-green-600 mt-2">
                            This is your official FBR invoice number. Save this for your records.
                          </p>
                        </div>
                      )}

                      {/* Show submission details */}
                      {submissionResult.submission.submissionResponse && (
                        <div className="bg-blue-100 p-3 rounded border border-blue-200">
                          <p className="text-sm text-blue-800">
                            <strong>Status:</strong> {submissionResult.submission.submissionResponse.statusCode === '00' ? 'Success' : 'Processed'}
                          </p>
                          {submissionResult.submission.submissionResponse.message && (
                            <p className="text-sm text-blue-800 mt-1">
                              <strong>Message:</strong> {submissionResult.submission.submissionResponse.message}
                            </p>
                          )}
                          <p className="text-sm text-blue-800 mt-1">
                            <strong>Submitted At:</strong> {new Date(submissionResult.submittedAt).toLocaleString()}
                          </p>
                        </div>
                      )}

                      {/* Updated invoice preview link */}
                      <div className="bg-yellow-100 p-3 rounded border border-yellow-200">
                        <p className="text-sm text-yellow-800">
                          <strong>📋“‹ Invoice Updated:</strong> Your invoice preview now shows the FBR-assigned invoice number and QR code.
                        </p>
                        <button
                          onClick={() => setActiveTab('preview')}
                          className="mt-2 px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors"
                        >
                          View Updated Invoice
                        </button>
                      </div>

                      {/* Saved invoice link */}
                      {submissionResult.savedInvoiceId && (
                        <div className="bg-green-100 p-3 rounded border border-green-200">
                          <p className="text-sm text-green-800">
                            <strong>📋’¾ Invoice Saved:</strong> Your invoice has been saved to your invoices list with PDF download and print capabilities.
                          </p>
                          <div className="mt-2 space-x-2">
                            <button
                              onClick={() => window.open(`/invoices/${submissionResult.savedInvoiceId}`, '_blank')}
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                            >
                              View in Invoices
                            </button>
                            <button
                              onClick={() => window.open('/invoices', '_blank')}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                            >
                              Go to Invoices List
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Error Messages */
                    <div className="space-y-3">
                      <div className="text-red-700">
                        <p className="font-medium text-lg"> Submission Failed</p>
                        <p className="text-sm mt-1">Please review the error details below:</p>
                      </div>

                      {/* Show main error message */}
                      {submissionResult.submission?.error && (
                        <div className="bg-red-100 p-3 rounded border border-red-200">
                          <p className="text-sm text-red-800 font-medium">Error:</p>
                          <p className="text-sm text-red-800">{submissionResult.submission.error}</p>
                        </div>
                      )}

                      {/* Show FBR submission response errors */}
                      {submissionResult.submission?.submissionResponse && (
                        <div className="bg-red-100 p-3 rounded border border-red-200">
                          <p className="text-sm text-red-800 font-medium">FBR Response:</p>
                          <p className="text-sm text-red-800">
                            <strong>Status Code:</strong> {submissionResult.submission.submissionResponse.statusCode}
                          </p>
                          {submissionResult.submission.submissionResponse.errorCode && (
                            <p className="text-sm text-red-800">
                              <strong>Error Code:</strong> {submissionResult.submission.submissionResponse.errorCode}
                            </p>
                          )}
                          {submissionResult.submission.submissionResponse.error && (
                            <p className="text-sm text-red-800">
                              <strong>Error:</strong> {submissionResult.submission.submissionResponse.error}
                            </p>
                          )}
                          {submissionResult.submission.submissionResponse.message && (
                            <p className="text-sm text-red-800">
                              <strong>Message:</strong> {submissionResult.submission.submissionResponse.message}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Show raw response for debugging if needed */}
                      <details className="mt-4">
                        <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                          Show Technical Details
                        </summary>
                        <div className="mt-2 bg-gray-100 p-3 rounded border">
                          <pre className="text-xs text-gray-700 overflow-auto max-h-32">
                            {JSON.stringify(submissionResult.submission, null, 2)}
                          </pre>
                        </div>
                      </details>
                    </div>
                  )}

                  {submissionResult.submission?.success && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => downloadJSON(submissionResult, `submission-result-${submissionResult.scenarioId}-${Date.now()}.json`)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Download Result
                      </button>
                      <button
                        onClick={resetForm}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Create Another Invoice
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Buyer Management Popup */}
      {showBuyerPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            {/* Popup Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800">Buyer Management</h2>
              <button
                onClick={() => {
                  setShowBuyerPopup(false);
                  setBuyerSearchTerm('');
                  setShowAddBuyerForm(false);
                }}
                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Popup Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Search and Add New Buyer Controls */}
              <div className="flex gap-3 mb-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search buyers by name, NTN/CNIC, or address..."
                    value={buyerSearchTerm}
                    onChange={(e) => handleBuyerSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <button
                  onClick={selectWalkInCustomer}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors whitespace-nowrap"
                >
                  👤 Walk In Customer
                </button>
                <button
                  onClick={() => setShowAddBuyerForm(!showAddBuyerForm)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  {showAddBuyerForm ? '📋 Show List' : '➕ Add New'}
                </button>
              </div>

              {/* Add New Buyer Form */}
              {showAddBuyerForm && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
                  <h3 className="font-medium text-green-800 mb-3">Add New Buyer</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        NTN/CNIC *
                      </label>
                      <input
                        type="text"
                        value={newBuyerForm.buyerNTNCNIC}
                        onChange={(e) => setNewBuyerForm({ ...newBuyerForm, buyerNTNCNIC: e.target.value })}
                        placeholder="Enter NTN/CNIC"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Name *
                      </label>
                      <input
                        type="text"
                        value={newBuyerForm.buyerBusinessName}
                        onChange={(e) => setNewBuyerForm({ ...newBuyerForm, buyerBusinessName: e.target.value })}
                        placeholder="Enter business name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Province
                      </label>
                      <select
                        value={newBuyerForm.buyerProvince}
                        onChange={(e) => setNewBuyerForm({ ...newBuyerForm, buyerProvince: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        {referenceData.province?.map(option => (
                          <option key={option.code} value={option.description}>{option.description}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <input
                        type="text"
                        value={newBuyerForm.buyerAddress}
                        onChange={(e) => setNewBuyerForm({ ...newBuyerForm, buyerAddress: e.target.value })}
                        placeholder="Enter address"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Registration Type
                      </label>
                      <select
                        value={newBuyerForm.buyerRegistrationType}
                        onChange={(e) => setNewBuyerForm({ ...newBuyerForm, buyerRegistrationType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        {referenceData.registration_type?.map(option => (
                          <option key={option.code} value={option.description}>{option.description}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleAddNewBuyer}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      📋Save & Select
                    </button>
                    <button
                      onClick={() => {
                        setNewBuyerForm({
                          buyerNTNCNIC: '',
                          buyerBusinessName: '',
                          buyerProvince: 'Punjab',
                          buyerAddress: '',
                          buyerRegistrationType: 'Unregistered'
                        });
                      }}
                      className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                    >
                      📋 Clear
                    </button>
                  </div>
                </div>
              )}

              {/* Saved Buyers List */}
              {!showAddBuyerForm && (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-medium text-gray-800">
                      Saved Buyers ({filteredBuyers.length})
                    </h3>
                    {savedBuyers.length > 0 && (
                      <div className="text-sm text-gray-600">
                        {buyerSearchTerm ? `Showing ${filteredBuyers.length} of ${savedBuyers.length}` : `${savedBuyers.length} total`}
                      </div>
                    )}
                  </div>

                  {filteredBuyers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {buyerSearchTerm ? (
                        <div>
                          <p className="text-lg mb-2">📋  No buyers found</p>
                          <p>No buyers match your search "{buyerSearchTerm}"</p>
                          <button
                            onClick={() => setBuyerSearchTerm('')}
                            className="mt-2 text-blue-600 hover:text-blue-800 underline"
                          >
                            Clear search
                          </button>
                        </div>
                      ) : (
                        <div>
                          <p className="text-lg mb-2">📋 No saved buyers</p>
                          <p>Click "Add New" to create your first buyer</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {filteredBuyers.map((buyer) => (
                        <div
                          key={buyer.id}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex-1">
                            <div className="font-medium text-gray-800">
                              {buyer.business_name}
                              {buyer.customer_type === 'walk_in' && (
                                <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                  Walk-in
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              NTN/CNIC: {buyer.ntn_cnic}  {buyer.province}
                            </div>
                            {buyer.address && (
                              <div className="text-sm text-gray-500">
                                📋 {buyer.address}
                              </div>
                            )}
                            <div className="text-xs text-gray-400 mt-1">
                              {buyer.registration_type} Added: {new Date(buyer.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => selectBuyer(buyer)}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                            >
                              Select
                            </button>
                            {buyer.customer_type !== 'walk_in' && (
                              <button
                                onClick={() => {
                                  if (window.confirm(`Delete buyer "${buyer.business_name}"?`)) {
                                    deleteBuyerFromDatabase(buyer.id);
                                  }
                                }}
                                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                              >
                                📋
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Popup Footer */}
            <div className="flex justify-between items-center p-4 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                📋 Tip: Buyers are saved in your account database for quick access across all devices
              </div>
              <button
                onClick={() => {
                  setShowBuyerPopup(false);
                  setBuyerSearchTerm('');
                  setShowAddBuyerForm(false);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenarioInvoiceGenerator;
